import {
    createColumnHelper,
} from "@tanstack/react-table";

import {
    FiCheck,
    FiEye,
    FiPlay,
    FiSend,
    FiTrash2,
    FiXCircle,
    FiEdit2,
    FiMapPin,
    FiDollarSign,
    FiInr,
} from "react-icons/fi";

import {
    Link,
} from "react-router-dom";


const columnHelper =
    createColumnHelper();


// ============================================================
// FORMAT NUMBER
// ============================================================

const formatNumber = (
    value
) => {

    const number =
        Number(
            value || 0
        );

    return Number.isFinite(
        number
    )
        ? number.toFixed(2)
        : "0.00";

};


// ============================================================
// STATUS BADGE
// ============================================================

const StatusBadge = ({
    status,
}) => {

    let className =
        "bg-gray-100 text-gray-700";


    switch (status) {

        case "planned":

            className =
                "bg-yellow-100 text-yellow-700";

            break;


        case "started":

            className =
                "bg-indigo-100 text-indigo-700";

            break;


        case "submitted":

        case "pending":

            className =
                "bg-blue-100 text-blue-700";

            break;


        case "approved":

            className =
                "bg-green-100 text-green-700";

            break;


        case "rejected":

            className =
                "bg-red-100 text-red-700";

            break;


        case "correction_requested":

            className =
                "bg-orange-100 text-orange-700";

            break;


        case "in_progress":

            className =
                "bg-indigo-100 text-indigo-700";

            break;


        case "not_submitted":

            className =
                "bg-gray-100 text-gray-700";

            break;


        case "cancelled":

            className =
                "bg-gray-200 text-gray-600";

            break;


        default:

            className =
                "bg-gray-100 text-gray-700";

            break;

    }


    return (

        <span
            className={`
                inline-flex
                rounded-full
                px-3
                py-1
                text-xs
                font-semibold
                capitalize
                ${className}
            `}
        >

            {
                status
                    ? status.replace(
                        /_/g,
                        " "
                    )
                    : "-"
            }

        </span>

    );

};


// ============================================================
// OVERVIEW COLUMNS
// ============================================================
//
// Salesperson-wise overview:
//
// - Salesperson
// - Visits
// - Total KM
// - Locations
// - Total Expense
// - Approval
// - View
//
// ============================================================

export const overviewColumns = ({
    onView,
}) => {

    return [

        // ====================================================
        // SALESPERSON
        // ====================================================

        columnHelper.display({

            id:
                "salesperson",

            header:
                "Salesperson",

            cell: ({
                row,
            }) => {

                const item =
                    row.original;


                return (

                    <div>

                        <div
                            className="
                                font-semibold
                                text-gray-800
                            "
                        >

                            {
                                item.salespersonName ||
                                "-"
                            }

                        </div>


                        {
                            item.employeeCode && (

                                <div
                                    className="
                                        mt-1
                                        text-xs
                                        text-gray-500
                                    "
                                >

                                    {
                                        item.employeeCode
                                    }

                                </div>

                            )
                        }

                    </div>

                );

            },

        }),


        // ====================================================
        // VISIT COUNT
        // ====================================================

        columnHelper.display({

            id:
                "visitCount",

            header:
                "Visits",

            cell: ({
                row,
            }) => {

                const count =
                    row.original?.visitCount ||
                    0;


                return (

                    <span
                        className="
                            font-semibold
                            text-gray-800
                        "
                    >

                        {count}

                    </span>

                );

            },

        }),


        // ====================================================
        // TOTAL KM
        // ====================================================

        columnHelper.display({

            id:
                "totalKm",

            header:
                "Total KM",

            cell: ({
                row,
            }) => {

                return (

                    <div
                        className="
                            flex
                            items-center
                            gap-1
                            font-medium
                            text-gray-700
                        "
                    >

                        <FiMapPin
                            className="
                                text-gray-500
                            "
                        />

                        {
                            formatNumber(
                                row.original?.totalKm
                            )
                        }

                        {" km"}

                    </div>

                );

            },

        }),


        // ====================================================
        // LOCATIONS
        // ====================================================

        columnHelper.display({

            id:
                "locations",

            header:
                "Locations",

            cell: ({
                row,
            }) => {

                const locations =
                    Array.isArray(
                        row.original?.locations
                    )
                        ? row.original.locations
                        : [];


                if (
                    locations.length === 0
                ) {

                    return "-";

                }


                return (

                    <div
                        className="
                            max-w-[260px]
                        "
                    >

                        <div
                            className="
                                truncate
                                text-sm
                                text-gray-700
                            "
                            title={
                                locations.join(
                                    ", "
                                )
                            }
                        >

                            {
                                locations.join(
                                    ", "
                                )
                            }

                        </div>


                        {
                            locations.length > 1 && (

                                <div
                                    className="
                                        mt-1
                                        text-xs
                                        text-gray-400
                                    "
                                >

                                    {
                                        locations.length
                                    }

                                    {" locations"}

                                </div>

                            )
                        }

                    </div>

                );

            },

        }),


        // ====================================================
        // TOTAL EXPENSE
        // ====================================================

        columnHelper.display({

            id:
                "totalExpense",

            header:
                "Total Expense",

            cell: ({
                row,
            }) => {

                const expense =
                    row.original?.totalExpense ??
                    row.original?.claimedExpense ??
                    0;


                return (

                    <div
                        className="
                            flex
                            items-center
                            gap-1
                            font-medium
                            text-gray-700
                        "
                    >

                        <span className="text-gray-600 font-medium">₹</span>

                        {
                            formatNumber(
                                expense
                            )
                        }

                    </div>

                );

            },

        }),


        // ====================================================
        // APPROVAL STATUS
        // ====================================================

        columnHelper.display({

            id:
                "approvalStatus",

            header:
                "Approval",

            cell: ({
                row,
            }) => {

                return (

                    <StatusBadge
                        status={
                            row.original?.approvalStatus
                        }
                    />

                );

            },

        }),


        // ====================================================
        // VIEW SALESPERSON
        // ====================================================

        columnHelper.display({

            id:
                "view",

            header:
                "View",

            cell: ({
                row,
            }) => {

                const employeeId =
                    row.original?.employeeId;


                if (
                    !employeeId
                ) {

                    return "-";

                }


                return (

                    <button
                        type="button"
                        onClick={() => {

                            if (
                                typeof onView ===
                                "function"
                            ) {

                                onView(
                                    employeeId
                                );

                            }

                        }}
                        className="
                            inline-flex
                            items-center
                            gap-2
                            rounded-lg
                            bg-gray-700
                            px-3
                            py-2
                            text-xs
                            font-medium
                            text-white
                            hover:bg-gray-800
                        "
                        title="View Salesperson Visits"
                    >

                        <FiEye />

                        View

                    </button>

                );

            },

        }),

    ];

};


// ============================================================
// VISIT COLUMNS
// ============================================================
//
// ROLE / ACTION FLOW
//
// SALESPERSON
// ------------------------------------------------------------
// View
// Start
// Submit
// Correct after correction request
// Cancel planned/correction-requested
//
// ADMIN / REVIEWER
// ------------------------------------------------------------
// View
// Approve
// Reject
// Request Correction
//
// IMPORTANT:
// Admin/reviewer does NOT get:
// - Start
// - Submit
// - Cancel
//
// Salesperson does NOT get:
// - Approve
// - Reject
// - Request Correction
//
// ============================================================

export const visitColumns = ({
    onDelete,
    onStart,
    onSubmit,
    onApprove,
    onReject,
    onCorrection,
    onEdit,
}) => {


    // ========================================================
    // CURRENT USER
    // ========================================================

    let user = {};


    try {

        user =
            JSON.parse(
                localStorage.getItem(
                    "user"
                ) || "{}"
            );

    } catch (error) {

        console.error(
            "Unable to parse logged-in user.",
            error
        );

        user = {};

    }


    // ========================================================
    // ROLES
    // ========================================================

    const roles =
        Array.isArray(
            user?.roles
        )
            ? user.roles
            : [];


    // ========================================================
    // PERMISSIONS
    // ========================================================

    const permissions =
        Array.isArray(
            user?.permissions
        )
            ? user.permissions
            : [];


    // ========================================================
    // PERMISSION HELPER
    // ========================================================

    const hasPermission = (
        permission
    ) => {

        return permissions.includes(
            permission
        );

    };


    // ========================================================
    // ROLE HELPERS
    // ========================================================

    const isAdmin =
        roles.includes(
            "admin"
        );


    const isSuperAdmin =
        roles.includes(
            "super_admin"
        );


    const isSalesPerson =
        roles.includes(
            "sales_person"
        );


    // ========================================================
    // SALESPERSON ACTIONS
    // ========================================================
    //
    // ONLY SALESPERSON CAN:
    //
    // - Start
    // - Submit
    // - Cancel
    // - Correct
    //
    // Even if Admin has visits.manage permission,
    // these actions remain hidden for Admin.
    //
    // ========================================================

    const canSalespersonUpdate =
        isSalesPerson &&
        (
            hasPermission(
                "visits.update"
            ) ||
            hasPermission(
                "visits.manage"
            )
        );


    const canSalespersonDelete =
        isSalesPerson &&
        (
            hasPermission(
                "visits.delete"
            ) ||
            hasPermission(
                "visits.manage"
            )
        );


    // ========================================================
    // REVIEWER / ADMIN ACTIONS
    // ========================================================
    //
    // Admin / Super Admin:
    // Always allowed to review.
    //
    // Other reviewer roles:
    // Need visits.approve or visits.manage.
    //
    // Salesperson:
    // NEVER allowed.
    //
    // ========================================================

    const canApprove =
        !isSalesPerson &&
        (
            isAdmin ||
            isSuperAdmin ||
            hasPermission(
                "visits.approve"
            ) ||
            hasPermission(
                "visits.manage"
            )
        );


    const canReject =
        canApprove;


    const canRequestCorrection =
        canApprove;


    // ========================================================
    // DEBUG
    // ========================================================

    console.log(
        "VISIT RBAC",
        {
            roles,
            permissions,

            isAdmin,
            isSuperAdmin,
            isSalesPerson,

            canSalespersonUpdate,
            canSalespersonDelete,

            canApprove,
            canReject,
            canRequestCorrection,
        }
    );


    return [

        // ====================================================
        // DATE
        // ====================================================

        columnHelper.accessor(
            "visitDate",
            {

                header:
                    "Date",

                cell: (
                    info
                ) => {

                    const value =
                        info.getValue();


                    if (!value) {

                        return "-";

                    }


                    return new Date(
                        value
                    ).toLocaleDateString();

                },

            }
        ),


        // ====================================================
        // EMPLOYEE
        // ====================================================

        columnHelper.accessor(
            "employee.name",
            {

                header:
                    "Employee",

                cell: (
                    info
                ) => (

                    <span
                        className="
                            font-semibold
                        "
                    >

                        {
                            info.getValue() ||
                            "-"
                        }

                    </span>

                ),

            }
        ),


        // ====================================================
        // TYPE
        // ====================================================

        columnHelper.accessor(
            "visitType",
            {

                header:
                    "Type",

                cell: (
                    info
                ) => (

                    <span
                        className="
                            capitalize
                        "
                    >

                        {
                            info.getValue() ||
                            "-"
                        }

                    </span>

                ),

            }
        ),


        // ====================================================
        // CUSTOMER
        // ====================================================

        columnHelper.display({

            id:
                "customer",

            header:
                "Customer",

            cell: ({
                row,
            }) => {

                const visit =
                    row.original;


                if (
                    visit.visitType ===
                    "school"
                ) {

                    return (
                        visit.school?.name ||
                        "-"
                    );

                }


                if (
                    visit.visitType ===
                    "party"
                ) {

                    return (
                        visit.party?.name ||
                        "-"
                    );

                }


                return "-";

            },

        }),


        // ====================================================
        // PURPOSE
        // ====================================================

        columnHelper.accessor(
            "purpose",
            {

                header:
                    "Purpose",

                cell: (
                    info
                ) => {

                    return (
                        info.getValue() ||
                        "-"
                    );

                },

            }
        ),


        // ====================================================
        // STATUS
        // ====================================================

        columnHelper.accessor(
            "status",
            {

                header:
                    "Status",

                cell: (
                    info
                ) => (

                    <StatusBadge
                        status={
                            info.getValue()
                        }
                    />

                ),

            }
        ),


        // ====================================================
        // JOURNEY PLAN
        // ====================================================

        columnHelper.display({

            id:
                "journeyPlan",

            header:
                "Journey Plan",

            cell: ({
                row,
            }) => {

                const visit =
                    row.original;


                if (
                    !visit.journeyPlanItemId
                ) {

                    return (

                        <span
                            className="
                                text-xs
                                text-gray-400
                            "
                        >

                            Not Linked

                        </span>

                    );

                }


                return (

                    <span
                        className="
                            rounded-full
                            bg-purple-100
                            px-2.5
                            py-1
                            text-xs
                            font-medium
                            text-purple-700
                        "
                    >

                        Linked

                    </span>

                );

            },

        }),


        // ====================================================
        // ACTIONS
        // ====================================================

        columnHelper.display({

            id:
                "actions",

            header:
                "Actions",

            cell: ({
                row,
            }) => {

                const visit =
                    row.original;


                return (

                    <div
                        className="
                            flex
                            items-center
                            gap-2
                            whitespace-nowrap
                        "
                    >

                        {/* ==================================
                            VIEW
                        ================================== */}

                        <Link
                            to={
                                `/admin/visits/${visit.id}`
                            }
                            className="
                                rounded
                                bg-gray-600
                                p-2
                                text-white
                                hover:bg-gray-700
                            "
                            title="View Visit"
                        >

                            <FiEye />

                        </Link>


                        {/* ==================================
                            SALESPERSON:
                            START VISIT
                        ================================== */}

                        {
                            canSalespersonUpdate &&
                            visit.status ===
                                "planned" &&
                            typeof onStart ===
                                "function" &&

                            (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onStart(
                                            visit.id
                                        )
                                    }
                                    className="
                                        rounded
                                        bg-indigo-600
                                        p-2
                                        text-white
                                        hover:bg-indigo-700
                                    "
                                    title="Start Visit"
                                >

                                    <FiPlay />

                                </button>

                            )
                        }


                        {/* ==================================
                            SALESPERSON:
                            SUBMIT VISIT
                        ================================== */}

                        {
                            canSalespersonUpdate &&
                            visit.status ===
                                "started" &&
                            typeof onSubmit ===
                                "function" &&

                            (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onSubmit(
                                            visit.id
                                        )
                                    }
                                    className="
                                        rounded
                                        bg-blue-600
                                        p-2
                                        text-white
                                        hover:bg-blue-700
                                    "
                                    title="Submit Visit"
                                >

                                    <FiSend />

                                </button>

                            )
                        }


                    {/* ==================================SALESPERSON: EDIT / CORRECT VISIT================================== */}

                    {canSalespersonUpdate &&
                        ["started", "correction_requested"].includes(
                            visit.status
                        ) &&
                        typeof onEdit === "function" && (
                            <button
                                type="button"
                                onClick={() => onEdit(visit)}
                                className="
                                    rounded
                                    bg-orange-500
                                    p-2
                                    text-white
                                    hover:bg-orange-600
                                    transition-colors
                                "
                                title={
                                    visit.status === "started"
                                        ? "Edit Visit"
                                        : "Correct Visit"
                                }
                                aria-label={
                                    visit.status === "started"
                                        ? "Edit Visit"
                                        : "Correct Visit"
                                }
                            >
                                <FiEdit2 />
                            </button>
                        )}


                        {/* ==================================
                            SALESPERSON:
                            CANCEL VISIT
                        ================================== */}

                        {
                            canSalespersonDelete &&
                            (
                                visit.status ===
                                    "planned" ||

                                visit.status ===
                                    "correction_requested"
                            ) &&
                            typeof onDelete ===
                                "function" &&

                            (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onDelete(
                                            visit.id
                                        )
                                    }
                                    className="
                                        rounded
                                        bg-red-600
                                        p-2
                                        text-white
                                        hover:bg-red-700
                                    "
                                    title="Cancel Visit"
                                >

                                    <FiTrash2 />

                                </button>

                            )
                        }


                        {/* ==================================
                            ADMIN / REVIEWER:
                            APPROVE
                        ================================== */}

                        {
                            canApprove &&
                            visit.status ===
                                "submitted" &&
                            typeof onApprove ===
                                "function" &&

                            (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onApprove(
                                            visit.id
                                        )
                                    }
                                    className="
                                        rounded
                                        bg-green-600
                                        p-2
                                        text-white
                                        hover:bg-green-700
                                    "
                                    title="Approve Visit"
                                >

                                    <FiCheck />

                                </button>

                            )
                        }


                        {/* ==================================
                            ADMIN / REVIEWER:
                            REJECT
                        ================================== */}

                        {
                            canReject &&
                            visit.status ===
                                "submitted" &&
                            typeof onReject ===
                                "function" &&

                            (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onReject(
                                            visit.id
                                        )
                                    }
                                    className="
                                        rounded
                                        bg-red-800
                                        p-2
                                        text-white
                                        hover:bg-red-900
                                    "
                                    title="Reject Visit"
                                >

                                    <FiXCircle />

                                </button>

                            )
                        }


                        {/* ==================================
                            ADMIN / REVIEWER:
                            REQUEST CORRECTION
                        ================================== */}

                        {
                            canRequestCorrection &&
                            visit.status ===
                                "submitted" &&
                            typeof onCorrection ===
                                "function" &&

                            (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onCorrection(
                                            visit.id
                                        )
                                    }
                                    className="
                                        rounded
                                        bg-orange-500
                                        p-2
                                        text-white
                                        hover:bg-orange-600
                                    "
                                    title="Request Correction"
                                >

                                    <FiEdit2 />

                                </button>

                            )
                        }

                    </div>

                );

            },

        }),

    ];

};